import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { emailTemplatesAPI, EmailTemplate, TemplateCategory, TemplateVariables } from '../../services/api';
import { ArrowLeftIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Spinner } from '../ui';

const EmailTemplateForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    category: 'CUSTOM' as TemplateCategory,
    isActive: true,
    description: '',
    variables: [] as string[],
  });
  const [loading, setLoading] = useState(false);
  const [availableVariables, setAvailableVariables] = useState<TemplateVariables | null>(null);
  const [showVariables, setShowVariables] = useState(false);

  useEffect(() => {
    fetchAvailableVariables();
    if (isEdit) {
      fetchTemplate();
    }
  }, [id]);

  const fetchAvailableVariables = async () => {
    try {
      const variables = await emailTemplatesAPI.getVariables();
      setAvailableVariables(variables);
    } catch (error) {
      console.error('Error fetching variables:', error);
    }
  };

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const template = await emailTemplatesAPI.getById(parseInt(id!));
      setFormData({
        name: template.name,
        subject: template.subject,
        body: template.body,
        category: template.category,
        isActive: template.isActive,
        description: template.description || '',
        variables: template.variables || [],
      });
    } catch (error) {
      console.error('Error fetching template:', error);
      alert('Failed to load template');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await emailTemplatesAPI.update(parseInt(id!), formData);
      } else {
        await emailTemplatesAPI.create(formData);
      }
      navigate('/clients');
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('body') as HTMLTextAreaElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.body;
    const before = text.substring(0, start);
    const after = text.substring(end);
    
    setFormData(prev => ({
      ...prev,
      body: before + variable + after,
    }));

    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  if (loading && isEdit) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/clients')}
          className="inline-flex items-center text-sm text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Clients
        </button>
        <h1 className="text-3xl font-bold text-neutral-900">
          {isEdit ? 'Edit Email Template' : 'Create Email Template'}
        </h1>
        <p className="mt-2 text-sm text-neutral-600">
          Create reusable email templates with dynamic variables
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form */}
        <div className="lg:col-span-2">
          <Card variant="elevated">
            <CardBody className="p-8">
              <form onSubmit={handleSubmit}>
                {/* Template Name */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="e.g., Welcome Email"
                  />
                </div>

                {/* Category & Status */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Category *
                    </label>
                    <select
                      required
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as TemplateCategory }))}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="WELCOME">Welcome</option>
                      <option value="FOLLOW_UP">Follow Up</option>
                      <option value="PROPOSAL">Proposal</option>
                      <option value="THANK_YOU">Thank You</option>
                      <option value="REMINDER">Reminder</option>
                      <option value="NEWSLETTER">Newsletter</option>
                      <option value="CUSTOM">Custom</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Status
                    </label>
                    <select
                      value={formData.isActive.toString()}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Brief description of this template"
                  />
                </div>

                {/* Subject */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Email Subject *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="e.g., Welcome to {{companyName}}, {{clientName}}!"
                  />
                </div>

                {/* Body */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-neutral-700">
                      Email Body *
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowVariables(!showVariables)}
                      className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                    >
                      <SparklesIcon className="h-4 w-4 mr-1" />
                      {showVariables ? 'Hide' : 'Show'} Variables
                    </button>
                  </div>
                  <textarea
                    id="body"
                    required
                    rows={12}
                    value={formData.body}
                    onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors font-mono text-sm"
                    placeholder="Dear {{clientName}},&#10;&#10;Welcome to our service!&#10;&#10;Best regards,&#10;{{userName}}"
                  />
                  <p className="mt-2 text-xs text-neutral-500">
                    Use variables like {`{{clientName}}`} to insert dynamic content
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-4 pt-6 border-t border-neutral-200">
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={loading}
                    className="flex-1"
                  >
                    {isEdit ? 'Update Template' : 'Create Template'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/clients')}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </div>

        {/* Variables Sidebar */}
        {showVariables && availableVariables && (
          <div className="lg:col-span-1">
            <Card variant="elevated" className="sticky top-8">
              <CardBody className="p-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4">Available Variables</h3>

                {/* Client Variables */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-neutral-700 mb-2">Client</h4>
                  <div className="space-y-1">
                    {availableVariables.client.map((variable) => (
                      <button
                        key={variable}
                        type="button"
                        onClick={() => insertVariable(variable)}
                        className="block w-full text-left px-3 py-2 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors font-mono"
                      >
                        {variable}
                      </button>
                    ))}
                  </div>
                </div>

                {/* User Variables */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-neutral-700 mb-2">User</h4>
                  <div className="space-y-1">
                    {availableVariables.user.map((variable) => (
                      <button
                        key={variable}
                        type="button"
                        onClick={() => insertVariable(variable)}
                        className="block w-full text-left px-3 py-2 text-sm bg-success-50 text-success-700 rounded-lg hover:bg-success-100 transition-colors font-mono"
                      >
                        {variable}
                      </button>
                    ))}
                  </div>
                </div>

                {/* System Variables */}
                <div>
                  <h4 className="text-sm font-medium text-neutral-700 mb-2">System</h4>
                  <div className="space-y-1">
                    {availableVariables.system.map((variable) => (
                      <button
                        key={variable}
                        type="button"
                        onClick={() => insertVariable(variable)}
                        className="block w-full text-left px-3 py-2 text-sm bg-secondary-50 text-secondary-700 rounded-lg hover:bg-secondary-100 transition-colors font-mono"
                      >
                        {variable}
                      </button>
                    ))}
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailTemplateForm;

